/**
 * Authentication Service
 * Handles Azure AD authentication and role detection
 */

import { PublicClientApplication, AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from 'axios';
import { User, UserRole } from '../types/User.types';
import { msalConfig, loginRequest, graphConfig } from '../config/authConfig';
import { API_BASE_URL } from '../config/api';

export const ACCESS_DENIED_ERROR = 'ACCESS_DENIED';

// Module-level singleton — created once, never recreated
const msalInstance = new PublicClientApplication(msalConfig);

class AuthService {
  // Shared promise: all concurrent callers (e.g. React 18 StrictMode double-invoke)
  // wait for the same initialization so handleRedirectPromise() runs exactly once
  // and isAuthenticated() is only checked after tokens are in the MSAL cache.
  private initPromise: Promise<void> | null = null;

  private get isMock(): boolean {
    return import.meta.env.VITE_ENV === 'development' &&
      import.meta.env.VITE_AZURE_CLIENT_ID === 'mock-client-id-for-testing';
  }

  /**
   * Initialize MSAL and process any pending redirect response.
   * Called once from initializeAuth() on every page load.
   *
   * Uses a shared promise so concurrent callers (React 18 StrictMode runs effects
   * twice) all wait for the SAME handleRedirectPromise() call. Without this,
   * the second concurrent caller would see handleRedirectPromise()=null (code
   * already consumed) and isAuthenticated()=false, causing a stuck loading state
   * that only clears on page refresh.
   */
  async initialize(): Promise<void> {
    if (this.isMock) return;
    if (!this.initPromise) {
      this.initPromise = this._doInitialize();
    }
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    await msalInstance.initialize();
    try {
      // Process the auth code Microsoft appended to the URL after loginRedirect.
      // This is the step that exchanges the code for tokens and stores the account.
      const result = await msalInstance.handleRedirectPromise();
      // Store the access token returned by the redirect (preferred), so the very
      // first API call after login has a bearer token without waiting for the
      // request-interceptor to re-acquire one.
      if (result?.accessToken) {
        sessionStorage.setItem('token', result.accessToken);
      }
    } catch (err) {
      // Non-fatal: can happen with stale hash fragments or state mismatches.
      // Reset the promise so the next page load can retry cleanly.
      this.initPromise = null;
      console.warn('[Auth] handleRedirectPromise error (non-fatal):', err);
    }
  }

  /**
   * Login with Azure AD
   */
  async login(): Promise<void> {
    try {
      // TEMPORARY: Mock login for local testing without Azure AD
      // TODO: Remove this mock and uncomment Azure AD login for production
      if (this.isMock) {
        console.warn('🚨 Using MOCK authentication - Azure AD is not configured');
        
        // Create and store mock account immediately
        const mockAccount = {
          homeAccountId: 'mock-user-123',
          environment: 'mock',
          tenantId: 'mock-tenant',
          username: 'sumit.singh@provana.com',
          localAccountId: 'mock-user-123',
          name: 'Sumit Kumar Singh',
          idTokenClaims: {},
        };
        localStorage.setItem('mockAccount', JSON.stringify(mockAccount));
        
        return; // Mock login successful
      }
      
      // Use redirect flow — more reliable than popup (popups are often blocked by browsers)
      await msalInstance.loginRedirect(loginRequest);
      // Browser navigates away to Microsoft login; code below this line does not run.
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Logout from Azure AD
   */
  async logout(): Promise<void> {
    // TEMPORARY: Mock logout for local testing
    if (this.isMock) {
      localStorage.removeItem('mockAccount');
      console.warn('🚨 Mock logout - Azure AD is not configured');
      return;
    }
    
    sessionStorage.removeItem('token'); // Clear stored token
    sessionStorage.removeItem('userCache'); // Clear cached user/role
    const account = this.getAccount();
    await msalInstance.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: window.location.origin + '/login',
    });
  }

  /**
   * Get current Azure AD account
   */
  getAccount(): AccountInfo | null {
    // TEMPORARY: Mock account for local testing
    if (this.isMock) {
      // Return mock account
      const mockAccount = localStorage.getItem('mockAccount');
      if (mockAccount) {
        return JSON.parse(mockAccount) as AccountInfo;
      }
      return null;
    }
    
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getAccount() !== null;
  }

  /**
   * Get a bearer token to send to OUR backend API.
   *
   * Strategy (new, simpler scheme):
   *  - We acquire a Microsoft Graph access token (scope: User.Read).
   *  - The backend validates it by calling Graph /me directly, so whether MSAL
   *    hands us a JWT or an opaque token, it works.
   *  - We store it in localStorage['token'] so the axios interceptor can read it.
   *  - We return the same value so callers that need a Graph token also work.
   */
  async getAccessToken(): Promise<string | null> {
    // In mock mode, return a mock token immediately
    if (this.isMock) {
      const mockToken = 'mock-access-token-for-development';
      sessionStorage.setItem('token', mockToken);
      return mockToken;
    }

    const account = this.getAccount();
    if (!account) return null;

    try {
      const graphResult = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read'],
        account,
      });

      const token = graphResult.accessToken;
      if (token) {
        sessionStorage.setItem('token', token);
        console.log('[Auth] Graph access token stored (length:', token.length, ')');
      } else {
        console.warn('[Auth] acquireTokenSilent returned empty access token');
      }
      return token || null;
    } catch (error) {
      // InteractionRequiredAuthError (AADSTS160021 etc.) means the Azure AD session
      // is gone — silent refresh is impossible. Redirect to login so the user can
      // re-authenticate rather than silently returning a stale/expired token.
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('[Auth] Azure AD session expired — redirecting to login:', error);
        sessionStorage.removeItem('token');
        await msalInstance.loginRedirect(loginRequest);
        return null; // loginRedirect navigates away; this line is never reached
      }
      // Non-interaction errors (network blip etc.) — fall back to cached token as before
      console.warn('[Auth] Silent token acquisition failed, using sessionStorage fallback:', error);
      const cached = sessionStorage.getItem('token');
      return cached || null;
    }
  }

  /**
   * Get user profile from Microsoft Graph
   */
  async getUserProfile(): Promise<any> {
    const token = await this.getAccessToken();
    if (!token) throw new Error('No access token available');

    try {
      const response = await axios.get(graphConfig.graphMeEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get user profile:', error);
      throw error;
    }
  }

  /**
   * Get user profile photo from Microsoft Graph
   */
  async getUserPhoto(): Promise<string | null> {
    const token = await this.getAccessToken();
    if (!token) return null;

    try {
      const response = await axios.get(graphConfig.graphMePhotoEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(response.data);
      return url;
    } catch (error) {
      // User might not have a profile photo
      console.warn('No profile photo available');
      return null;
    }
  }

  /**
   * Fetch user role from backend API
   * This makes API call to backend to get role based on email
   */
  async getUserWithRole(): Promise<User> {
    // TEMPORARY: Mock user for local testing without Azure AD
    if (this.isMock) {
      console.warn('🚨 Using MOCK user data - Azure AD is not configured');

      const mockRole  = localStorage.getItem('mockRole')      || 'SUPERVISOR';
      const mockEmail = localStorage.getItem('mockUserEmail') || 'sumit.singh@provana.com';
      const mockName  = localStorage.getItem('mockUserName')  || '';

      // Pre-configured personas (must match MockAuthenticationHandler.cs)
      const personas: Record<string, User> = {
        'sumit.singh@provana.com': {
          id: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d',
          email: 'sumit.singh@provana.com',
          name: 'Sumit Kumar Singh',
          role: UserRole.SUPERVISOR,
          employeeId: '101807',
          department: 'BPM',
          isActive: true,
        },
        'harpreet.sharma@provana.com': {
          id: 'f6a7b8c9-d0e1-4f5a-c03b-4c5d6e7f8a9c',
          email: 'harpreet.sharma@provana.com',
          name: 'Harpreet Sharma',
          role: UserRole.MANAGER,
          employeeId: '101807',
          department: 'BPM',
          isActive: true,
        },
        'priya.mehta@provana.com': {
          id: 'c3d4e5f6-a7b8-4c5d-ae1f-2a3b4c5d6e7f',
          email: 'priya.mehta@provana.com',
          name: 'Priya Mehta',
          role: UserRole.SUPERVISOR,
          employeeId: '101808',
          department: 'BPM',
          isActive: true,
        },
        'ankit.verma@provana.com': {
          id: 'd4e5f6a7-b8c9-4d5e-bf2a-3b4c5d6e7f8a',
          email: 'ankit.verma@provana.com',
          name: 'Ankit Verma',
          role: UserRole.SUPERVISOR,
          employeeId: '101809',
          department: 'BPM',
          isActive: true,
        },
        'rahul.sharma@provana.com': {
          id: 'b2c3d4e5-f6a7-4b5c-9d0e-1f2a3b4c5d6e',
          email: 'rahul.sharma@provana.com',
          name: 'Rahul Sharma',
          role: UserRole.MANAGER,
          employeeId: '100001',
          department: 'BPM',
          isActive: true,
        },
        'neha.gupta@provana.com': {
          id: 'e5f6a7b8-c9d0-4e5f-c03b-4c5d6e7f8a9b',
          email: 'neha.gupta@provana.com',
          name: 'Neha Gupta',
          role: UserRole.MANAGER,
          employeeId: '100002',
          department: 'BPM',
          isActive: true,
        },
      };

      const resolveMockRole = (r: string): UserRole => {
        if (r === 'ADMIN')   return UserRole.ADMIN;
        if (r === 'MANAGER') return UserRole.MANAGER;
        return UserRole.SUPERVISOR;
      };

      if (personas[mockEmail]) {
        // Override role in case it was changed in the panel independently
        const persona = { ...personas[mockEmail], role: resolveMockRole(mockRole) };
        return persona;
      }

      // Custom email fallback
      const displayName = mockName || mockEmail.split('@')[0].replace('.', ' ');
      return {
        id: mockEmail, // use email as surrogate id for custom users
        email: mockEmail,
        name: displayName.replace(/\b\w/g, c => c.toUpperCase()),
        role: resolveMockRole(mockRole),
        employeeId: '',
        department: 'BPM',
        isActive: true,
      };
    }
    
    const account = this.getAccount();
    if (!account) throw new Error('No account found');

    // Get access token (also stores the ID token in localStorage for data API calls)
    const accessToken = await this.getAccessToken();

    // ── Call backend /api/auth/user with the Graph access token ──────────────
    // The backend validates the token against Graph API and upserts the user
    // record in the Users table with Name, Email, JobTitle, Department, EmployeeId.
    if (accessToken) {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/user`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = response.data.user;
        console.info('[Auth] User synced to DB:', data.email, '| Role:', data.role, '| EmployeeId:', data.employeeId);
        // Map backend role string (e.g. "MANAGER", "SUPERVISOR") to frontend UserRole enum
        const backendRoleMap: Record<string, UserRole> = {
          'SUPERVISOR':        UserRole.SUPERVISOR,
          'MANAGER':           UserRole.MANAGER,
          'OPERATIONS_MANAGER': UserRole.OPERATIONS_MANAGER,
          'DIRECTOR':          UserRole.DIRECTOR,
          'ADMIN':             UserRole.ADMIN,
          'EMPLOYEE':          UserRole.EMPLOYEE,
          'OPEX':              UserRole.OPEX,
        };
        const resolvedRole = backendRoleMap[(data.role ?? '').toUpperCase()] ?? UserRole.SUPERVISOR;
        return {
          id:         data.azureId || account.homeAccountId,
          email:      data.email,
          name:       data.name,
          role:       resolvedRole,
          employeeId: data.employeeId ?? undefined,
          department: data.department ?? undefined,
          isActive:   true,
        };
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 403) {
            throw new Error(ACCESS_DENIED_ERROR);
          }
          if (err.response && err.response.status >= 500) {
            throw err; // Server error — do not fall back to MSAL (fail-closed)
          }
        }
        console.warn('[Auth] Backend user sync failed, falling back to MSAL account:', err);
      }
    }

    // ── Fallback: build user from MSAL account alone (no DB upsert) ──────────
    return {
      id:       account.homeAccountId,
      email:    account.username,
      name:     account.name || account.username,
      role:     UserRole.SUPERVISOR,
      isActive: true,
    };
  }

  /**
   * Get redirect path based on user role
   */
  getRedirectPathForRole(role: UserRole): string {
    switch (role) {
      case UserRole.ADMIN:
        return '/admin';
      case UserRole.OPEX:
        return '/reports';
      case UserRole.SUPERVISOR:
        return '/dashboard';
      case UserRole.EMPLOYEE:
        return '/employee/my-work';
      default:
        return '/dashboard';
    }
  }
}

export const authService = new AuthService();
export default authService;

